import { useState } from 'react';
import { useDealer, useInvestor, useUpdateDealer, useUpdateInvestor } from '../api/hooks';
import type { PartyType } from '../types';

/**
 * The account/wallet number a transaction form shows once payment method isn't Cash. There is no
 * per-transaction column for this — it lives on the investor/dealer profile — so the field just
 * displays that saved value and, if edited here, writes the change back to the profile rather than
 * to the transaction being recorded.
 */
export function useTransactionAccountField(partyType: PartyType, partyId: number) {
  const investorQuery = useInvestor(partyType === 'Investor' ? partyId : 0);
  const dealerQuery = useDealer(partyType === 'Dealer' ? partyId : 0);
  const updateInvestor = useUpdateInvestor(partyId);
  const updateDealer = useUpdateDealer(partyId);

  const [value, setValue] = useState<string | undefined>(undefined);
  const [touched, setTouched] = useState(false);

  const savedAccount =
    partyType === 'Investor' ? investorQuery.data?.bankAccount ?? '' : dealerQuery.data?.accountNumber ?? '';

  const currentValue = touched ? value ?? '' : savedAccount;

  const onChange = (next: string) => {
    setValue(next);
    setTouched(true);
  };

  /** Call after a transaction succeeds: persists the edited number onto the profile, if it changed. */
  const commitIfChanged = async () => {
    if (!touched || currentValue === savedAccount) return;

    if (partyType === 'Investor' && investorQuery.data) {
      await updateInvestor.mutateAsync({ ...investorQuery.data, bankAccount: currentValue });
    } else if (partyType === 'Dealer' && dealerQuery.data) {
      await updateDealer.mutateAsync({ ...dealerQuery.data, accountNumber: currentValue });
    }
  };

  return { value: currentValue, onChange, commitIfChanged };
}
